// Scoring engine for Financial MRI
import { toNum } from './format';

const RISK_MAP = {
  q1: { sell: 0, wait: 50, invest: 100 },
  q2: { capital: 20, income: 50, wealth: 80 },
  q3: { '<3': 20, '3-7': 60, '7plus': 100 },
  q4: { '<30': 20, '30-60': 60, '60-80': 80, '80plus': 100 }
};

export const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

export const computeRiskScore = (s) => {
  const r1 = RISK_MAP.q1[s.risk_q1];
  const r2 = RISK_MAP.q2[s.risk_q2];
  const r3 = RISK_MAP.q3[s.risk_q3];
  const r4 = RISK_MAP.q4[s.risk_q4];
  const vals = [r1, r2, r3, r4].filter((x) => x !== undefined);
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
};

export const riskLabel = (score) => {
  if (score < 35) return 'Conservative';
  if (score < 65) return 'Moderate';
  return 'Aggressive';
};

// AMFI-permitted illustrative return rate for MF projections.
export const ILLUSTRATIVE_RATE = 0.125;

// Scenario rates for expectation-setting. Capped at the AMFI-permitted 12.5%
// illustrative rate. NEVER project above this — compliance rule.
export const SCENARIOS = [
  { key: 'conservative', label: 'Conservative', rate: 0.10, tone: 'amber' },
  { key: 'illustrative', label: 'AMFI Illustrative', rate: 0.125, tone: 'indigo' }
];

export const computeSipAtRate = (goal, rate) => {
  const amount = toNum(goal.amount);
  const years = toNum(goal.years);
  if (!amount || !years) return 0;
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return Math.round(amount / n);
  const sip = (amount * r) / (Math.pow(1 + r, n) - 1);
  return Math.round(sip);
};

export const computeLumpsumAtRate = (goal, rate) => {
  const amount = toNum(goal.amount);
  const years = toNum(goal.years);
  if (!amount || !years) return 0;
  const pv = amount / Math.pow(1 + rate, years);
  return Math.round(pv);
};

export const computeSipNeeded = (goal) => computeSipAtRate(goal, ILLUSTRATIVE_RATE);
export const computeLumpsumNeeded = (goal) => computeLumpsumAtRate(goal, ILLUSTRATIVE_RATE);

export const computeScores = (s) => {
  const monthlyIncome = toNum(s.monthly_income);
  const monthlyExpense = toNum(s.monthly_expense);
  const monthlyEMI = toNum(s.monthly_emi);
  const monthlySip = toNum(s.monthly_sip);
  const liquidSavings = toNum(s.liquid_savings);
  const lifeCover = toNum(s.life_cover);
  const healthCover = toNum(s.health_cover);
  const mfValue = toNum(s.mf_value);
  const equityValue = toNum(s.equity_value);
  const fdValue = toNum(s.fd_value);
  const realEstate = toNum(s.realestate_value);
  const totalLoans = toNum(s.total_loans);
  const ppfNps = toNum(s.ppf_nps_epf);
  const otherRet = toNum(s.other_retire_sav);
  const monthlyRetireNeed = toNum(s.monthly_retire_need);
  const age = toNum(s.age);
  const retireAge = toNum(s.retire_age) || 60;
  const yearsToRetire = Math.max(1, retireAge - age);
  const sec80c = toNum(s.sec_80c);
  const sec80d = toNum(s.sec_80d);

  const totalAssets = mfValue + equityValue + fdValue + realEstate;
  const netWorth = totalAssets - totalLoans;

  // Emergency fund score
  const monthlyOutflow = monthlyExpense + monthlyEMI;
  const efMonths = monthlyOutflow > 0 ? liquidSavings / monthlyOutflow : 0;
  const efScore = clamp((efMonths / 6) * 100);

  // Insurance score
  const lcNeeded = monthlyIncome * 12 * 10;
  const lcPct = lcNeeded > 0 ? clamp((lifeCover / lcNeeded) * 100) : 0;
  const hcPct = clamp((healthCover / 1000000) * 100);
  const insScore = clamp(lcPct * 0.4 + hcPct * 0.3 + (s.has_term ? 20 : 0) + (s.has_critical ? 10 : 0));

  // Retirement score
  const corpusNeeded = monthlyRetireNeed * 12 * 25;
  const retireSavings = ppfNps + otherRet + mfValue * 0.5;
  const projected = monthlySip * 12 * yearsToRetire * 1.5 + retireSavings * Math.pow(1.08, yearsToRetire);
  const retScore = corpusNeeded > 0 ? clamp((projected / corpusNeeded) * 100) : 0;

  // Risk score
  const riskScore = computeRiskScore(s);

  // Goal score
  const totalSipNeeded = (s.goals || []).reduce((acc, g) => acc + computeSipNeeded(g), 0);
  const goalScore = totalSipNeeded > 0
    ? clamp((monthlySip / totalSipNeeded) * 80 + 20)
    : (monthlySip > 0 ? 60 : 20);

  // Tax score
  const c80c = Math.min(sec80c, 150000);
  const c80d = Math.min(sec80d, 25000);
  const taxScore = clamp((c80c / 150000) * 60 + (c80d / 25000) * 25 + (s.tax_regime && s.tax_regime !== 'Not sure' ? 15 : 0));

  // Net worth score (assets vs liabilities + age-based benchmark)
  const nwBenchmark = monthlyIncome * 12 * Math.max(1, age - 25) * 0.5;
  const nwScore = nwBenchmark > 0 ? clamp((netWorth / nwBenchmark) * 100) : (netWorth > 0 ? 60 : 20);

  // Portfolio score
  const eqTargetByRisk = riskScore >= 65 ? 70 : riskScore >= 35 ? 55 : 35;
  const eqRangeMid = { '<20': 10, '20-40': 30, '40-60': 50, '60plus': 70 }[s.equity_pct] ?? -1;
  const rebalScore = { '<6m': 100, '6-12m': 75, '1-2y': 40, 'never': 0 }[s.last_rebalance] ?? 30;
  const alignScore = eqRangeMid >= 0 ? clamp(100 - Math.abs(eqRangeMid - eqTargetByRisk) * 2) : 30;
  const portScore = clamp(alignScore * 0.6 + rebalScore * 0.4);

  const scores = {
    netWorth: Math.round(nwScore),
    risk: Math.round(riskScore),
    goals: Math.round(goalScore),
    insurance: Math.round(insScore),
    retirement: Math.round(retScore),
    emergency: Math.round(efScore),
    portfolio: Math.round(portScore),
    tax: Math.round(taxScore)
  };

  const scoreArr = Object.values(scores);
  const overall = Math.round(scoreArr.reduce((a, b) => a + b, 0) / scoreArr.length);

  return {
    ...scores,
    overall,
    // derived values
    netWorthValue: netWorth,
    totalAssets,
    monthlySurplus: monthlyIncome - monthlyExpense - monthlyEMI,
    savingsRate: monthlyIncome > 0 ? ((monthlyIncome - monthlyExpense - monthlyEMI) / monthlyIncome) * 100 : 0,
    lcNeeded,
    lcGap: Math.max(0, lcNeeded - lifeCover),
    hcGap: Math.max(0, 1000000 - healthCover),
    corpusNeeded,
    retireGap: Math.max(0, corpusNeeded - projected),
    projectedCorpus: projected,
    efMonths,
    totalSipNeeded,
    sipGap: Math.max(0, totalSipNeeded - monthlySip),
    yearsToRetire,
    riskLabel: riskLabel(riskScore)
  };
};

export const scoreMeta = [
  { key: 'netWorth', label: 'Net Worth' },
  { key: 'risk', label: 'Risk Profile' },
  { key: 'goals', label: 'Goal Coverage' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'retirement', label: 'Retirement' },
  { key: 'emergency', label: 'Emergency Fund' },
  { key: 'portfolio', label: 'Portfolio Health' },
  { key: 'tax', label: 'Tax Efficiency' }
];

// Priority actions
export const priorityActions = (s, computed) => {
  const actions = [];
  if (computed.insurance < 50) {
    actions.push({
      title: 'Increase Life & Health Insurance Cover',
      why: `Life cover gap of \u20B9${(computed.lcGap/100000).toFixed(1)}L vs 10x income benchmark.`,
      timeline: 'Within 30 days',
      category: 'Pure Term Plan + Family Floater Health'
    });
  }
  if (computed.emergency < 50) {
    actions.push({
      title: 'Build Emergency Fund to 6 Months of Expenses',
      why: `Currently ${computed.efMonths.toFixed(1)} months of cover \u2014 below the 6-month benchmark.`,
      timeline: 'Within 6\u201312 months',
      category: 'Liquid Fund / Overnight Fund category'
    });
  }
  if (computed.goals < 50 && computed.sipGap > 0) {
    actions.push({
      title: 'Bridge SIP Gap Across Stated Goals',
      why: `Current SIP of \u20B9${(toNum(s.monthly_sip)/1000).toFixed(0)}K falls short of \u20B9${(computed.totalSipNeeded/1000).toFixed(0)}K needed across ${s.goals.length} goals.`,
      timeline: 'Next SIP cycle',
      category: 'Diversified Equity Fund / Hybrid Fund categories'
    });
  }
  if (computed.retirement < 50) {
    actions.push({
      title: 'Step Up Retirement SIP',
      why: `Projected corpus of \u20B9${(computed.projectedCorpus/10000000).toFixed(2)}Cr falls short of \u20B9${(computed.corpusNeeded/10000000).toFixed(2)}Cr needed.`,
      timeline: 'Within 3 months',
      category: 'Retirement-oriented Multi-Cap / NPS'
    });
  }
  if (computed.tax < 50) {
    actions.push({
      title: 'Maximise 80C / 80D Deductions',
      why: 'Unused deduction headroom available before 31 March.',
      timeline: 'Before Q4',
      category: 'ELSS category, Health Insurance premium (80D)'
    });
  }
  if (computed.portfolio < 50) {
    actions.push({
      title: 'Review & Rebalance Portfolio',
      why: 'Portfolio allocation deviating from risk-appropriate mix or rebalancing overdue.',
      timeline: 'Within 60 days',
      category: 'Asset re-allocation across existing fund categories'
    });
  }
  return actions.slice(0, 6);
};
